import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const InviteCodeToggle = () => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempInviteCode, setTempInviteCode] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        ?.from('site_settings')
        ?.select('*')
        ?.single();

      if (error) throw error;

      setSettings(data);
      setTempInviteCode(data?.invite_code || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
      setErrorMessage('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleRequired = async () => {
    try {
      setIsSaving(true);
      setErrorMessage('');
      
      const newValue = !settings?.invite_code_required;
      
      const { error } = await supabase
        ?.from('site_settings')
        ?.update({ 
          invite_code_required: newValue,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', settings?.id);

      if (error) throw error;

      setSettings(prev => ({ ...prev, invite_code_required: newValue }));
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error updating setting:', error);
      setErrorMessage('Failed to update setting');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveInviteCode = async () => {
    try {
      setIsSaving(true);
      setErrorMessage('');

      if (tempInviteCode?.trim()?.length !== 6) {
        setErrorMessage('Invite code must be exactly 6 digits');
        setIsSaving(false);
        return;
      }

      if (!/^\d{6}$/?.test(tempInviteCode)) {
        setErrorMessage('Invite code must contain only numbers');
        setIsSaving(false);
        return;
      }

      const { error } = await supabase
        ?.from('site_settings')
        ?.update({ 
          invite_code: tempInviteCode,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', settings?.id);

      if (error) throw error;

      setSettings(prev => ({ ...prev, invite_code: tempInviteCode }));
      setIsEditing(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error updating invite code:', error);
      setErrorMessage('Failed to update invite code');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setTempInviteCode(settings?.invite_code || '');
    setIsEditing(false);
    setErrorMessage('');
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-lg p-6 border border-border">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Key" size={20} className="text-accent" />
            Registration Invite Code
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Control registration access with a six-digit invite code
          </p>
        </div>
      </div>

      {showSuccessMessage && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-md">
          <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">Settings updated successfully!</p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-error/10 border border-error/20 rounded-md">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errorMessage}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Toggle Required */}
        <div className="flex items-center justify-between p-4 bg-surface-dark/30 rounded-lg border border-border/50">
          <div>
            <p className="font-medium text-foreground">Require Invite Code</p>
            <p className="text-sm text-muted-foreground">
              When enabled, new users must enter the invite code to register
            </p>
          </div>
          <button
            onClick={handleToggleRequired}
            disabled={isSaving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
              settings?.invite_code_required ? 'bg-accent' : 'bg-border'
            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.invite_code_required ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Invite Code Management */}
        <div className="p-4 bg-surface-dark/30 rounded-lg border border-border/50">
          <div className="flex items-center justify-between mb-4">
            <p className="font-medium text-foreground">Six-Digit Invite Code</p>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={isSaving}
              >
                <Icon name="Edit" size={16} />
                Edit Code
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <Input
                type="text"
                value={tempInviteCode}
                onChange={(e) => {
                  const value = e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
                  setTempInviteCode(value);
                  setErrorMessage('');
                }}
                placeholder="Enter 6-digit code"
                maxLength={6}
                disabled={isSaving}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveInviteCode}
                  loading={isSaving}
                  disabled={isSaving || tempInviteCode?.length !== 6}
                >
                  <Icon name="Save" size={16} />
                  Save Code
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="px-4 py-2 bg-accent/10 rounded-md font-mono text-lg text-accent tracking-wider">
                {settings?.invite_code || '------'}
              </div>
              {!settings?.invite_code && (
                <span className="text-sm text-muted-foreground">(No code set)</span>
              )}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              <strong className="text-foreground">How it works:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>When enabled, the registration page will require users to enter this code</li>
              <li>The invite code must be exactly 6 digits</li>
              <li>Users cannot complete registration without the correct code</li>
              <li>Only superadmin can change user roles after registration</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteCodeToggle;